import { useState } from 'react';
import type { CadJsonPayload } from '@/api/cadJson';
import { useProjectViewStore } from '@/stores/projectView';

interface CadMateriaalPreviewProps {
  projectId: number;
  taskId: number;
  faseName: string;
  payload: CadJsonPayload;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CadMateriaalPreview({ projectId, taskId, faseName, payload, onClose, onSuccess }: CadMateriaalPreviewProps) {
  const uploadMateriaallijst = useProjectViewStore((s) => s.uploadMateriaallijst);
  const uploadingMateriaal = useProjectViewStore((s) => s.uploadingMateriaal);
  const [localError, setLocalError] = useState<string | null>(null);

  const missing = payload.materiaal.filter((line) => line.productFound === false);
  // Include handmatig lines that have a real quantity AND a resolvable product
  // (e.g. afwijkend-systeem / staalnet). Only qty=0 placeholders and unknown
  // SKUs are excluded — NOT handmatig per se. See the buildCsv note below.
  const importable = payload.materiaal.filter((line) => line.aantal > 0 && line.productFound !== false);
  const canSubmit = importable.length > 0 && missing.length === 0 && !uploadingMateriaal;

  async function handleSubmit() {
    setLocalError(null);
    if (missing.length > 0) {
      setLocalError('Los onbekende artikelnummers op voor je importeert.');
      return;
    }

    const csv = buildCsv(payload);
    const fileName = payload.fileName.replace(/\.json$/i, '') || 'CAD JSON';
    const file = new File([csv], `${fileName}.csv`, { type: 'text/csv;charset=utf-8' });
    const success = await uploadMateriaallijst(projectId, taskId, file);
    if (success) {
      onSuccess?.();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-td-navy">Materiaal importeren</h3>
            <p className="text-xs text-td-muted mt-0.5">Naar fase: {faseName}</p>
          </div>
          <button type="button" onClick={onClose} className="text-td-muted hover:text-td-navy" aria-label="Sluiten">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          {payload.warning && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
              <strong className="font-semibold">Let op:</strong> {payload.warning.message}
            </div>
          )}
          {(localError || missing.length > 0) && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" role="alert">
              {localError ?? `${missing.length} artikelnummer${missing.length === 1 ? '' : 's'} niet gevonden in Odoo.`}
            </div>
          )}

          <div className="max-h-[58vh] overflow-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wide text-td-muted">
                <tr>
                  <th className="px-3 py-2">Artikelnummer</th>
                  <th className="px-3 py-2">Omschrijving</th>
                  <th className="px-3 py-2 text-right">Aantal</th>
                  <th className="px-3 py-2">Sectie</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {payload.materiaal.map((line, index) => {
                  const unknown = line.productFound === false;
                  return (
                    <tr key={`${line.artikelnummer}-${index}`} className={`border-t border-gray-100 ${unknown ? 'bg-red-50' : ''}`}>
                      <td className="px-3 py-2 font-mono text-xs text-td-navy">{line.artikelnummer}</td>
                      <td className="px-3 py-2 text-td-navy">{line.omschrijving}</td>
                      <td className="px-3 py-2 text-right text-td-navy">{line.aantal}</td>
                      <td className="px-3 py-2 text-td-muted">{line.sectie || '-'}</td>
                      <td className="px-3 py-2">
                        {unknown ? (
                          <span className="text-xs font-semibold text-red-600" title="SKU niet gevonden in Odoo">
                            Niet gevonden
                          </span>
                        ) : line.uploadArtikelnummer && line.uploadArtikelnummer !== line.artikelnummer ? (
                          <span className="text-xs font-semibold text-td-blue" title={`Uploadt als ${line.uploadArtikelnummer}`}>
                            Zonder spaties
                          </span>
                        ) : line.isHandmatig ? (
                          <span className="rounded bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Handmatig</span>
                        ) : (
                          <span className="text-xs text-td-muted">OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy">
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50"
            >
              {uploadingMateriaal ? 'Importeren...' : `Toevoegen aan ${faseName}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildCsv(payload: CadJsonPayload): string {
  const lines = ['Interne referentie;Aantal'];
  for (const item of payload.materiaal) {
    // Skip only qty=0 placeholders (backend rejects qty<=0) and unknown SKUs
    // (backend rejects PRODUCT_NOT_FOUND). Handmatig lines with a real quantity
    // and a resolvable product — like staalnet/binddraad in AFW. SYSTEEM — must
    // be uploaded. The previous `item.isHandmatig ||` dropped them silently.
    if (!(item.aantal > 0) || item.productFound === false) continue;
    lines.push(`${item.uploadArtikelnummer ?? item.artikelnummer};${String(item.aantal).replace('.', ',')}`);
  }
  return `${lines.join('\n')}\n`;
}
